import React from "react";
import { useRouter } from "next/router";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import { Ff } from "@src/types.generated";
import Sparkles2OutlineIcon from "@src/lib/icons/12px/sparkles-2-outline";
import { XIcon } from "lucide-react";
import { useOrgFeatureFlagsQuery } from "../app/top-nav/SetOrgFeatureFlags.generated";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import useIsFullAdmin from "@src/features/app/hooks/useIsFullAdmin";

const AskOpusBanner: React.FC = () => {
  const router = useRouter();
  const [isDismissed, setIsDismissed] = React.useState(false);
  // Admins on the Opus Agent already have Ask Opus; the opt-in only adds
  // library search for trainees and managers.
  const opusAgentEnabled = useFeatureFlag(Ff.OpusAgent);
  const isFullAdmin = useIsFullAdmin();
  const forTeamOnly = opusAgentEnabled && isFullAdmin;
  const { data: orgFeatureFlagsData, loading } = useOrgFeatureFlagsQuery();
  const { userActionPermissions } = useActionPermissions();
  const isAskOpusEnabled = orgFeatureFlagsData?.OrgFeatureFlags?.find(
    (flag) => flag.name === Ff.ChatKnowledgeBase,
  )?.enabled;
  // Don't show banner if feature is enabled or if user dismissed it
  // The CTA opens /settings/ask-opus, which 404s without changeOrgSettings.
  if (
    isAskOpusEnabled ||
    isDismissed ||
    loading ||
    !userActionPermissions?.changeOrgSettings
  ) {
    return null;
  }

  const handleEnableClick = () => {
    router.push("/settings/ask-opus");
  };

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  return (
    <div
      className="flex items-center justify-between rounded-2xl border-2 p-4"
      style={{
        borderColor: "#E9EAEC",
        background:
          "linear-gradient(90deg, rgba(173,189,255,0.08) 0%, rgba(255,226,118,0.06) 100%), #FFF",
      }}
    >
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-blue-2">
            <Sparkles2OutlineIcon className="size-5 text-blue-9" />
          </div>
          <div className="flex flex-col">
            <Text variant="body-title" className="text-gray-12">
              {forTeamOnly
                ? "Let your team search your library with Ask Opus"
                : "Turn on Ask Opus for your team"}
            </Text>
            <Text variant="compact-description" className="text-gray-10">
              {forTeamOnly
                ? "Trainees and managers get instant answers from your training content"
                : "Get instant, AI-powered answers based on your content library"}
            </Text>
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-3">
        <Button size="sm" onClick={handleEnableClick}>
          {forTeamOnly ? "Set up" : "Try it out"}
        </Button>
        <Button
          variant="secondary"
          size="icon-md"
          onClick={handleDismiss}
          aria-label="Dismiss banner"
        >
          <XIcon />
        </Button>
      </div>
    </div>
  );
};

export default AskOpusBanner;
